import React, { memo, useMemo, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import format from 'date-fns/format';
import { toDate } from '@common/utils/date';
import { isNil } from '@common/utils';
import { SuitabilityHeader } from '@container/customer';

/**
 * 추천펀드 TD
 */
const FundTd = memo(({ invTypeText, targetInvTypeText, fundText }) => {
  const [invTypeTextPriority] = useState({
    위험회피형: 0,
    안정추구형: 1,
    위험중립형: 2,
    적극투자형: 3,
    위험선호형: 4,
  });

  const getIncludePriorityList = useCallback(
    (invTypeTextPriority, invTypeText) => {
      const currentPrioity = invTypeTextPriority[invTypeText];
      const fillArray = (from, to, acc) => {
        return acc.length === to
          ? acc
          : fillArray(from + 1, to, acc.concat(from));
      };
      const result = fillArray(0, currentPrioity, []);

      return result;
    },
    [],
  );

  return (
    <td
      className={`${
        getIncludePriorityList(invTypeTextPriority, invTypeText).includes(
          invTypeTextPriority[targetInvTypeText],
        )
          ? 'bg-blue'
          : ''
      }`}
      style={{ whiteSpace: 'pre-line' }}
    >
      {fundText.split('<br />').join('\n')}
    </td>
  );
});

FundTd.propTypes = {
  invTypeText: PropTypes.string.isRequired,
  targetInvTypeText: PropTypes.string.isRequired,
  fundText: PropTypes.string.isRequired,
};

/**
 * 추천 보험상품 목록
 */
const RecommandProductTable = memo(({ prdTypeText }) => (
  <div className="app-box">
    <h2 className="tit-con">추천 보험상품 목록</h2>

    <table className="tbl-sty3">
      <caption>목록</caption>
      <thead>
        <tr>
          <th scope="col" style={{ width: '10%' }}>
            구분
          </th>
          <th scope="col">고객성향</th>
          <th scope="col" style={{ width: '58%' }}>
            추천보험상품군
          </th>
        </tr>
      </thead>

      <tbody>
        <tr className={`${'보장형' === prdTypeText ? 'bg-blue' : ''}`}>
          <td className="text-center">보장형</td>
          <td>사망,질병 등을 대비한 보장자산 마련</td>
          <td className="text-center">변액종신</td>
        </tr>
        <tr className={`${'연금형' === prdTypeText ? 'bg-blue' : ''}`}>
          <td className="text-center">연금형</td>
          <td>노후 대비 등을 위한 목적자금 마련</td>
          <td className="text-center">변액연금</td>
        </tr>
        <tr className={`${'저축형' === prdTypeText ? 'bg-blue' : ''}`}>
          <td className="text-center">저축형</td>
          <td>장기적인 자산증식 목적자금 마련</td>
          <td className="text-center">변액유니버셜(적립형)</td>
        </tr>
        <tr className={`${'부적합' === prdTypeText ? 'bg-blue' : ''}`}>
          <td className="text-center">부적합</td>
          <td>단기재산증식목적, 원금손실불가 고객</td>
          <td className="text-center">변액보험가입불가</td>
        </tr>
      </tbody>
    </table>
  </div>
));

RecommandProductTable.propTypes = {
  prdTypeText: PropTypes.string.isRequired,
};

/**
 * 추천 펀드목록
 */
const RecommandFundTable = memo(({ userType, invTypeText }) => {
  return (
    <div className="app-box">
      <h2 className="tit-con">추천 펀드 목록</h2>
      <table className="tbl-sty3">
        <caption>목록</caption>
        <thead>
          <tr>
            <th scope="col" style={{ width: '12%' }}>
              구분
            </th>
            <th scope="col">고객성향</th>
            <th scope="col" style={{ width: '15%' }}>
              위험자산 편입비율
            </th>
            <th scope="col" style={{ width: '15%' }}>
              펀드 수익률 변동성
            </th>
          </tr>
        </thead>

        <tbody>
          <tr className={`${'위험회피형' === invTypeText ? 'bg-blue' : ''}`}>
            <td className="text-center">위험회피형</td>
            <td>원금손실을 원하지 않으며, 예.적금 수준의 수익률 기대</td>
            <td className="text-center">불가</td>
            <td className="text-center">5% 이하</td>
            {/* <FundTd
              invTypeText={invTypeText}
              targetInvTypeText="위험회피형"
              fundText="채권형"
            /> */}
          </tr>
          <tr className={`${'안정추구형' === invTypeText ? 'bg-blue' : ''}`}>
            <td className="text-center">안정추구형</td>
            <td>원금손실을 최소화하고, 이자.배당수익 등 안정적 수익 추구</td>
            <td className="text-center">30% 미만</td>
            <td className="text-center">10% 이하</td>
            {/* <FundTd
              invTypeText={invTypeText}
              targetInvTypeText="안정추구형"
              fundText="안정혼합형,<br />장기채권형,<br />미국장기회사채권형,<br />글로벌분산-채권형"
            /> */}
          </tr>
          <tr className={`${'위험중립형' === invTypeText ? 'bg-blue' : ''}`}>
            <td className="text-center">위험중립형</td>
            <td>
              원금손실 위험을 충분히 인식하고, 예.적금보다 높은 수익률을 위해
              일정수준의 손실 감내 가능
            </td>
            <td className="text-center">50% 미만</td>
            <td className="text-center">15% 이하</td>
            {/* <FundTd
              invTypeText={invTypeText}
              targetInvTypeText="위험중립형"
              fundText={
                '혼합형, 배당혼합형<br />롱텀밸류혼합형,<br />해외혼합형,<br />주식혼합형 (변액연금 연금재원미보증형)'
              }
            /> */}
          </tr>
          <tr className={`${'적극투자형' === invTypeText ? 'bg-blue' : ''}`}>
            <td className="text-center">적극투자형</td>
            <td>
              원금손실 위험을 감내하고 높은 수익률을 원하며, 상당부분 위험
              자산에 투자할 의향이 있음
            </td>
            <td className="text-center">70% 이하</td>
            <td className="text-center">25% 이하</td>
            {/* <FundTd
              invTypeText={invTypeText}
              targetInvTypeText="적극투자형"
              fundText="주식혼합형"
            /> */}
          </tr>
          <tr className={`${'위험선호형' === invTypeText ? 'bg-blue' : ''}`}>
            <td className="text-center">위험선호형</td>
            <td>
              아주 높은 수익률을 원하고, 대부분을 위험 자산에 투자할 의향 있음
            </td>
            <td className="text-center">70% 초과</td>
            <td className="text-center">25% 초과</td>
            {/* <FundTd
              invTypeText={invTypeText}
              targetInvTypeText="위험선호형"
              fundText="플러스알파인덱스주식형,<br />롱텀밸류주식형,<br />액티브주식형, 배당주식형,<br />이머징마켓주식형,<br />글로벌주식형,<br />스마트주식형,<br />달러미국주식형,<br />미국성장주식형,<br />유럽주식형,<br />아시아주식형,<br />글로벌테마Star-첨단미래산업,<br />글로벌테마Star-모빌리티, 글로벌테마Star-엔터테인먼트,<br />글로벌테마Star-바이오헬스,<br />글로벌테마Star-인프라부동산, 글로벌테마Star-원자재,<br />글로벌테마Star-ESG,<br />글로벌분산-테마Stars,<br />글로벌분산-주식형"
            /> */}
          </tr>
        </tbody>
      </table>

      <div className="info-box bg-yellow mgt-16 mgb-0">
        <p className="text-info3">
          단, VIP변액연금보험, 보증의힘변액연금보험은 채권형 펀드 비중에 따라
          분류함
        </p>
        <ul className="dot-type type-m">
          <li>위험회피형/안정추구형: 채권형 70%이상</li>
          <li>
            위험중립형/적극투자형/위험선호형: VIP변액연금보험 채권형 50%이상,
            보증의힘변액연금보험 채권형 60% 이상
          </li>
        </ul>
        <p className="text-info3 asterisk-type">
          상품별로 가입 가능 펀드가 상이하오니, 담당{' '}
          {`${$utils.checkLPText(userType) ? ' 라이프파트너' : ' 보험설계사'}`}
          에게 문의하시기 바랍니다.
        </p>
      </div>
    </div>
  );
});

RecommandFundTable.propTypes = {
  userType: PropTypes.string,
  invTypeText: PropTypes.string.isRequired,
};

/**
 * 진단결과 요약
 */
const ResultSummary = memo(({ prdTypeText, invTypeText }) => {
  return prdTypeText === '부적합' ? (
    <>
      <p className="text-con">
        고객님의 답변을 토대로 보험가입 목적,투자성향 등을 종합적으로 분석한
        결과 고객님의 보험 계약자 성향은 변액보험계약을 체결하는 것이
        <span class="text-large red bold">적합하지 않은 것</span>으로
        분석되었습니다.
      </p>

      <p className="text-con mgt-16">
        따라서,「 금융소비자 보호에 관한 법률」 제17조(적합성원칙)에 따라 우리
        회사는 고객님께 변액보험계약을 권유할 수 없습니다.
      </p>

      <p className="text-con mgt-16">
        다만, 고객님께서 변액보험 상품목록의 개별상품, 특별계정(펀드)을 선택하여
        상품구조 등 세부내용을 문의하실 경우, 그에 대한 답변은 가능합니다.
      </p>
    </>
  ) : (
    <p className="text-con">
      고객님의 답변을 종합적으로 분석한 결과, 고객님의 보험상품 성향은
      <span className="text-large blue bold">{` ${prdTypeText} `}</span>
      이며, 투자성향은
      <span className="text-large blue bold">{` ${invTypeText} `}</span>
      입니다.
      <br />
      고객님에게 적합한 보험계약은 아래와 같습니다.
    </p>
  );
});

ResultSummary.propTypes = {
  prdTypeText: PropTypes.string,
  invTypeText: PropTypes.string,
};

/**
 * 부적합 판단근거(적합성진단 주요 응답 결과)
 */
const NotSuitableResult = memo(
  ({ prdTypeText, invTypeText, legalAge, custKind, answers, testDate }) => {
    const getReasonListByAnswers = useCallback(
      (legalAge, custKind, answers) => {
        const isMinor = (legalAge, custKind) =>
          custKind !== 'C' && !isNil(legalAge) && 19 > Number(legalAge);
        const getReasons = (legalAge, custKind, answers) => {
          const reasonList = [];

          // 나이가 취약금융소비자 나이가 아닌데 취약 금융소비자 해댱 여부 예 체크시 부적합
          if (
            answers[0] !== 1 &&
            custKind !== 'C' &&
            65 > Number(legalAge) &&
            19 <= Number(legalAge)
          ) {
            reasonList.push(-1);
          } else if (
            answers[0] !== 1 &&
            // 만65세 이상 고령투자자는 10, 11 번 문항에 "투자경험 없음"으로 답변시
            ((custKind !== 'C' &&
              65 <= legalAge &&
              0 === answers[10] &&
              0 === answers[11]) ||
              // 미성년자 친권자/후견인이 작성하지 않은 경우(미체크시)
              isMinor(legalAge, custKind))
          ) {
            reasonList.push(0);
          }

          0 !== answers[1] && reasonList.push(1);
          3 === answers[2] && reasonList.push(2);
          4 === answers[7] && reasonList.push(7);
          4 === answers[8] && reasonList.push(8);
          0 === answers[12] && reasonList.push(12);
          0 === answers[14] && reasonList.push(14);

          return reasonList;
        };
        const result = getReasons(legalAge, custKind, answers);

        return result;
      },
      [],
    );

    return (
      <>
        <div className="app-box">
          <h2 className="tit-con">진단결과</h2>
          <table className="tbl-sty3">
            <caption>목록</caption>
            <tbody>
              <tr>
                <th
                  scope="row"
                  rowSpan="2"
                  style={{ width: '15%', verticalAlign: 'middle' }}
                >
                  변액보험
                </th>
                <th scope="row">보험상품 성향</th>
                <td style={{ width: '45%' }}>{prdTypeText}</td>
              </tr>
              <tr>
                <th scope="row">투자성향</th>
                <td style={{ width: '45%' }}>{invTypeText}</td>
              </tr>
              <tr>
                <th scope="row" style={{ width: '15%' }}>
                  진단일자
                </th>
                <th scope="row"></th>
                <td style={{ width: '45%' }}>
                  {format(toDate(testDate), 'yyyy년 MM월 dd일')}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="app-box">
          <h2 className="tit-con">
            부적합 판단근거(적합성진단 주요 응답 결과)
          </h2>
          <table className="tbl-sty3">
            <caption>목록</caption>
            <thead>
              <tr>
                <th scope="col" style={{ width: '22%' }}>
                  진단부문
                </th>
                <th scope="col" style={{ width: '27%' }}>
                  질문내용
                </th>
                <th scope="col" style={{ width: '10%' }}>
                  문항
                </th>
                <th scope="col">부적합 판단 기준</th>
              </tr>
            </thead>
            <tbody>
              {getReasonListByAnswers(legalAge, custKind, answers).includes(
                1,
              ) && (
                <tr className="bg-blue">
                  <td>보험가입목적</td>
                  <td>가입희망 금융상품</td>
                  <td className="text-center">1번</td>
                  <td>
                    {`"일반보험"또는 "주식,채권 등 금융투자상품"또는 "예금,적금"선택`}
                  </td>
                </tr>
              )}

              {getReasonListByAnswers(legalAge, custKind, answers).includes(
                2,
              ) && (
                <tr className="bg-blue">
                  <td>보험가입목적</td>
                  <td>변액보험 가입목적</td>
                  <td className="text-center">2번</td>
                  <td>{`"단기 재산증식"목적 선택`}</td>
                </tr>
              )}

              {getReasonListByAnswers(legalAge, custKind, answers).includes(
                7,
              ) && (
                <tr className="bg-blue">
                  <td>보험료 납입능력</td>
                  <td>보험료 납입 가능기간</td>
                  <td className="text-center">7번</td>
                  <td>{`"3년 미만"선택`}</td>
                </tr>
              )}

              {getReasonListByAnswers(legalAge, custKind, answers).includes(
                8,
              ) && (
                <tr className="bg-blue">
                  <td>보험계약 유지능력</td>
                  <td>보험계약 최소 유지기간</td>
                  <td className="text-center">8번</td>
                  <td>{`"7년 미만"선택`}</td>
                </tr>
              )}

              {getReasonListByAnswers(legalAge, custKind, answers).includes(
                12,
              ) && (
                <tr className="bg-blue">
                  <td>투자성향</td>
                  <td>투자시 핵심 고려사항</td>
                  <td className="text-center">12번</td>
                  <td>{`"원금은 반드시 보존되어야한다"선택`}</td>
                </tr>
              )}

              {getReasonListByAnswers(legalAge, custKind, answers).includes(
                14,
              ) && (
                <tr className="bg-blue">
                  <td>투자성향</td>
                  <td>손실감내수준</td>
                  <td className="text-center">14번</td>
                  <td>{`"어떠한 경우에도 손실이 나면 안된다"선택`}</td>
                </tr>
              )}

              {getReasonListByAnswers(legalAge, custKind, answers).includes(
                0,
              ) && (
                <tr className="bg-blue">
                  <td>취약금융소비자</td>
                  <td>취약금융소비자 여부</td>
                  <td className="text-center">-</td>
                  <td>
                    고령취약금융소비자 (만 65세이상)
                    <br />
                    10번 문항 “가입경험 없음” 또는 11번문항 “가입경험 없음” 선택
                    <br />
                  </td>
                </tr>
              )}

              {getReasonListByAnswers(legalAge, custKind, answers).includes(
                -1,
              ) && (
                <tr className="bg-blue">
                  <td>취약금융소비자</td>
                  <td>취약금융소비자 여부</td>
                  <td className="text-center">-</td>
                  <td>취약금융소비자 "예" 선택</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </>
    );
  },
);

NotSuitableResult.propTypes = {
  prdTypeText: PropTypes.string.isRequired,
  invTypeText: PropTypes.string.isRequired,
  legalAge: PropTypes.number.isRequired,
  custKind: PropTypes.string,
  answers: PropTypes.object.isRequired,
  testDate: PropTypes.string.isRequired,
};

/**
 * 변액보험 적합성 테스트 결과 확인
 */
const SuitabilityTestResult = ({
  customerInfo,
  suitabilityFormData,
  handleNotSuitableTest,
  pageConfirmClick,
  answers,
  isWriting, // 서면 진행 여부
}) => {
  const isNotSuitable = useMemo(
    () => '부적합' === suitabilityFormData.prdTypeText,
    [suitabilityFormData],
  );

  const { channelInfo } = useSelector(state => state.userReducer);

  /**
   * '1,2,3,4,5,1,1'형태의 답변을 {no:value}형태의 object로 변경
   */
  const deserializeAnswers = useCallback((str, delimeter = ',') => {
    const result = str.split(delimeter).reduce((acc, v, i) => {
      // acc[i] = i < 10 ? `0${i}0${v}` : `${i}0${v}`;
      acc[i] = Number(v);
      return acc;
    }, {});
    return result;
  }, []);

  return (
    <>
      <SuitabilityHeader title={'3. 변액보험 적합성 진단 결과 확인'} />
      <div className="con-wrap bg-gray">
        <div className="app-wrap">
          <div className="app-box">
            <h2 className="tit-con">변액보험 적합성 진단 결과</h2>
            <ResultSummary
              prdTypeText={suitabilityFormData.prdTypeText}
              invTypeText={suitabilityFormData.invTypeText}
            />
          </div>
          {isNotSuitable ? (
            <NotSuitableResult
              prdTypeText={suitabilityFormData.prdTypeText}
              invTypeText={suitabilityFormData.invTypeText}
              legalAge={customerInfo.legalAge}
              custKind={customerInfo.custKind}
              testDate={suitabilityFormData.testDate}
              answers={deserializeAnswers(answers, ',')}
            />
          ) : (
            <>
              <RecommandProductTable
                prdTypeText={suitabilityFormData.prdTypeText}
              />
              <RecommandFundTable
                invTypeText={suitabilityFormData.invTypeText}
                userType={channelInfo.cmnCdNm}
              />
            </>
          )}
        </div>
      </div>

      <div className="con-bottom bg-gray box-shadow">
        <div className="btn-area">
          <div className="box-btns-center">
            <button
              type="button"
              className="btn-s blue btn-wide"
              onClick={() =>
                isNotSuitable
                  ? handleNotSuitableTest()
                  : pageConfirmClick(
                      isWriting ? suitabilityFormData : () => true,
                    )
              }
            >
              {isNotSuitable ? '확인' : '결과 확인 및 작성 완료'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

SuitabilityTestResult.propTypes = {
  customerInfo: PropTypes.shape({
    legalAge: PropTypes.number.isRequired,
    custKind: PropTypes.string,
    answers: PropTypes.object,
  }).isRequired,
  suitabilityFormData: PropTypes.shape({
    reportSerialNo: PropTypes.string,
    prdTypeText: PropTypes.string,
    invTypeText: PropTypes.string,
    testDate: PropTypes.string.isRequired,
  }).isRequired,
  handleNotSuitableTest: PropTypes.func.isRequired,
  pageConfirmClick: PropTypes.func.isRequired,
  answers: PropTypes.string.isRequired,
  isWriting: PropTypes.bool,
};

SuitabilityTestResult.defaultProps = {
  isWriting: false,
};

export default memo(SuitabilityTestResult);
